// v2
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "./components/ui/sonner";
import ScrollToTop from "./components/ScrollToTop";
import { PWAComponents } from "./components/PWAComponents";
import { BuyerProvider } from "./contexts/BuyerContext";
import { ThemeProvider } from "./context/ThemeContext";
import { TenantProvider } from "./context/TenantContext";
import AccueilPorteBati from "./components/AccueilPorteBati";
import ModeVisiteur from "./components/ModeVisiteur";
import ErrorBoundary from "./components/ErrorBoundary";
import { ImmersiveScrollProvider } from "./context/ImmersiveScrollContext";
import { NavModeProvider } from "./context/NavModeContext";
import { AppModeProvider } from "./context/AppModeContext";
import DemoModeProvider from "./components/demo/DemoModeProvider";
import PushNotificationPrompt from "./components/PushNotificationPrompt";
import ResponsivePageWrapper from "./components/layout/ResponsivePageWrapper";
import CockpitFAB from "./components/layout/CockpitFAB";
import CockpitPreviewWidget from "./components/layout/CockpitPreviewWidget";
import GlobalFooter from "./components/layout/GlobalFooter";

// Pages
import HomePage from "./pages/HomePage";
import AbracadaWorldHome from "./pages/AbracadaWorldHome";
import SearchPage from "./pages/SearchPage";
import DecrireProjetPage from "./pages/DecrireProjetPage";
import OffreFondateurPage from "./pages/OffreFondateurPage";
import LandingPage from "./pages/LandingPage";
import EspaceProPage from "./pages/EspaceProPage";
import DigitalTeamPage from "./pages/DigitalTeamPage";
import NewClientMachinePage from "./pages/NewClientMachinePage";
import TransformationPage from "./pages/TransformationPage";
import AboutPage from "./pages/AboutPage";
import FAQPage from "./pages/FAQPage";
import BlogPage from "./pages/BlogPage";
import BlogPostPage from "./pages/BlogPostPage";
import ContactPage from "./pages/ContactPage";
import PricingPage from "./pages/PricingPage";
import SuperAssistantPage from "./pages/SuperAssistantPage";
import SalaryCostPage from "./pages/SalaryCostPage";
import DiagnosticEntrepreneurPage from "./pages/DiagnosticEntrepreneurPage";
import NosSolutionsPage from "./pages/NosSolutionsPage";

// Payment Pages (Stripe)
import PaymentSuccessPage from "./pages/PaymentSuccessPage";
import PaymentCancelledPage from "./pages/PaymentCancelledPage";

// Collab Pages
import JoinCollabPage from "./pages/JoinCollabPage";

// Feature Pages
import CRMBatimentPage from "./pages/features/CRMBatimentPage";
import DevisIntellPage from "./pages/features/DevisIntellPage";
import PipelinesPage from "./pages/features/PipelinesPage";
import GeolocalisationPage from "./pages/features/GeolocalisationPage";
import AssistantsPage from "./pages/features/AssistantsPage";
import AutomatisationPage from "./pages/features/AutomatisationPage";
import SuiviPaiementsPage from "./pages/features/SuiviPaiementsPage";

// Public Pages (No Layout)
import PublicQuoteSignPage from "./pages/public/PublicQuoteSignPage";
import ClientPortalPage from "./pages/public/ClientPortalPage";
import ProspectPortalPage from "./pages/public/ProspectPortalPage";
import ClientPortalDevisPage from "./pages/public/ClientPortalDevisPage";
import BoutiquePublicPage from "./pages/BoutiquePublicPage";
import JoinCirclePage from "./pages/JoinCirclePage";
import JoinGroupPage from "./pages/public/JoinGroupPage";
import PublicStorePage from "./pages/public/PublicStorePage";
import MyStorePage from "./pages/client/MyStorePage";

// Admin
import AdminLogin from "./pages/admin/AdminLogin";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminBlog from "./pages/admin/AdminBlog";
import AdminBlogEditor from "./pages/admin/AdminBlogEditor";
import AdminContacts from "./pages/admin/AdminContacts";
import AdminNewsletter from "./pages/admin/AdminNewsletter";
import AdminLeads from "./pages/admin/AdminLeads";
import AdminAdsPage from "./pages/admin/AdminAdsPage";

// Admin CRM
import AdminCRMDashboard from "./pages/admin/crm/AdminCRMDashboard";
import AdminProspectsPage from "./pages/admin/crm/AdminProspectsPage";
import AdminProspectDetailPage from "./pages/admin/crm/AdminProspectDetailPage";
import AdminPipelinePage from "./pages/admin/crm/AdminPipelinePage";
import AdminQuotesPage from "./pages/admin/crm/AdminQuotesPage";
import AdminClientsPage from "./pages/admin/crm/AdminClientsPage";

// Admin CRM - continued
import AdminTicketsPage from "./pages/admin/crm/AdminTicketsPage";
import AdminInvoicesPage from "./pages/admin/crm/AdminInvoicesPage";
import AdminAssistantsPage from "./pages/admin/resources/AdminAssistantsPage";
import AdminCandidatesPage from "./pages/admin/resources/AdminCandidatesPage";
import AdminTeamChatPage from "./pages/admin/resources/AdminTeamChatPage";
import AdminTasksPage from "./pages/admin/resources/AdminTasksPage";
import AdminClientChatPage from "./pages/admin/resources/AdminClientChatPage";

// Direction Commerciale
import CommercialDashboard from "./pages/admin/commercial/CommercialDashboard";
import PipelineKanban from "./pages/admin/commercial/PipelineKanban";

// Client Portal
import ClientLoginPage from "./pages/client/ClientLoginPage";
import ClientLayout from "./pages/client/ClientLayout";
import ClientDashboard from "./pages/client/ClientDashboard";
import ClientMessages from "./pages/client/ClientMessages";
import ClientTasks from "./pages/client/ClientTasks";
import CalendarPage from "./pages/client/CalendarPage";
import TeamPage from "./pages/client/TeamPage";
import CRMPage from "./pages/client/CRMPage";
import BillingPage from "./pages/client/BillingPage";
import UsersPage from "./pages/client/UsersPage";
import ClientTeamChatPage from "./pages/client/ClientTeamChatPage";
import BlogManagePage from "./pages/client/BlogManagePage";

// BâtiAssist - Module Prestations de Services Bâtiment
import BatiAssistHomePage from "./pages/client/crm-batiment/BatiAssistHomePage";
import CRMDashboard from "./pages/client/crm-batiment/CRMDashboard";
import ProspectsPage from "./pages/client/crm-batiment/ProspectsPage";
import CommercialPipeline from "./pages/client/crm-batiment/CommercialPipeline";
import ClientsPage from "./pages/client/crm-batiment/ClientsPage";
import DevisPage from "./pages/client/crm-batiment/DevisPage";
import FacturesPage from "./pages/client/crm-batiment/FacturesPage";
import ChantiersPage from "./pages/client/crm-batiment/ChantiersPage";
import CompteRenduChantierPage from "./pages/client/crm-batiment/CompteRenduChantierPage";
import PlanningPage from "./pages/client/crm-batiment/PlanningPage";
import RentabilitePage from "./pages/client/crm-batiment/RentabilitePage";
import RentabiliteDashboard from "./pages/client/crm-batiment/RentabiliteDashboard";
import DocumentsPage from "./pages/client/crm-batiment/DocumentsPage";
import ProduitsPage from "./pages/client/crm-batiment/ProduitsPage";
import PrestationsPage from "./pages/client/crm-batiment/PrestationsPage";
import TicketsSAVPage from "./pages/client/crm-batiment/TicketsSAVPage";
import BoutiqueProPage from "./pages/client/crm-batiment/BoutiqueProPage";

// Cockpit Pro (nouveau design)
import CockpitProLayout from "./pages/cockpit-pro/CockpitProLayout";
import CockpitProDashboard from "./pages/cockpit-pro/CockpitProDashboard";
import TachesProPage from "./pages/cockpit-pro/TachesProPage";
import ReseauProPage from "./pages/cockpit-pro/ReseauProPage";
import EmploiProPage from "./pages/cockpit-pro/EmploiProPage";
import ReseauSocialProPage from "./pages/cockpit-pro/ReseauSocialProPage";
import SocialHubPage from "./pages/cockpit-pro/reseaux-sociaux/SocialHubPage";
import DemandesDevisPage from "./pages/cockpit-pro/DemandesDevisPage";
import MessagerieProPage from "./pages/cockpit-pro/MessagerieProPage";
import ImportCSVPage from "./pages/cockpit-pro/catalogue/ImportCSVPage";

// Cockpit Pro - Collaboration/Équipes
import EquipeProPage from "./pages/cockpit-pro/collaboration/EquipeProPage";
import TachesEquipeProPage from "./pages/cockpit-pro/collaboration/TachesProPage";
import TchatProPage from "./pages/cockpit-pro/collaboration/TchatProPage";
import FeedProPage from "./pages/cockpit-pro/collaboration/FeedProPage";
import RejoindreEquipePage from "./pages/RejoindreEquipePage";
import CollabsProPage from "./pages/cockpit-pro/collaboration/CollabsProPage";

// Cockpit Pro - Pointage
import TimeClockPage from "./pages/cockpit/TimeClockPage";
import TimeClockManagerPage from "./pages/cockpit/TimeClockManagerPage";

// Settings
import NotificationSettingsPage from "./pages/client/NotificationSettingsPage";
import ConnectedAccountsPage from "./pages/client/ConnectedAccountsPage";
import HourlyRatesPage from "./pages/client/HourlyRatesPage";
// Pointage
import PointagePage from "./pages/client/PointagePage";

// Marketing
import MarketingDashboard from "./pages/client/marketing/MarketingDashboard";
import SocialAccountsPage from "./pages/client/marketing/SocialAccountsPage";
import ContentCalendarPage from "./pages/client/marketing/ContentCalendarPage";
import EngagementPage from "./pages/client/marketing/EngagementPage";
import AIAgentsPage from "./pages/client/marketing/AIAgentsPage";

// AI Vendor
import AIVendorDashboard from "./pages/client/ai-vendor/AIVendorDashboard";
import CatalogPage from "./pages/client/ai-vendor/CatalogPage";
import VendorSettingsPage from "./pages/client/ai-vendor/VendorSettingsPage";
import ConversationsPage from "./pages/client/ai-vendor/ConversationsPage";
import WidgetPage from "./pages/client/ai-vendor/WidgetPage";

// Client Boutique
import BoutiqueDashboard from "./pages/client/boutique/BoutiqueDashboard";
import BoutiqueDesignPage from "./pages/client/boutique/BoutiqueDesignPage";
import BoutiqueMediasPage from "./pages/client/boutique/BoutiqueMediasPage";
import BoutiqueAvisPage from "./pages/client/boutique/BoutiqueAvisPage";

// Email
import EmailPage from "./pages/client/EmailPage";

// Calendar
import SharedCalendarPage from "./pages/client/SharedCalendarPage";

// Ecosystem
import EcosystemLayout from "./components/ecosystem/EcosystemLayout";
import ReseauLayout from "./components/ecosystem/ReseauLayout";
import FeedPage from "./pages/ecosystem/social/FeedPage";
import ReelsPage from "./pages/ecosystem/social/ReelsPage";
import DiscoverPage from "./pages/ecosystem/social/DiscoverPage";
import ProfilePage from "./pages/ecosystem/social/ProfilePage";
import CreatePostPage from "./pages/ecosystem/social/CreatePostPage";
import PostDetailPage from "./pages/ecosystem/social/PostDetailPage";
import MessagesPage from "./pages/ecosystem/social/MessagesPage";
import EcoLoginPage from "./pages/ecosystem/social/LoginPage";
import EcoRegisterPage from "./pages/ecosystem/social/RegisterPage";
import ConfirmEmailPage from "./pages/ecosystem/social/ConfirmEmailPage";

// Page Actu style Facebook
import ActuPage from "./pages/ActuPage";

// Assistant IA Full Page (style Claude/ChatGPT)
import AssistantFullPage from "./pages/assistant/AssistantFullPage";

// Page Réseau Mobile
import ReseauMobilePage from "./pages/reseau/ReseauMobilePage";
import MessagerieReseauPage from "./pages/reseau/MessagerieReseauPage";
import CreateStoryPage from "./pages/reseau/CreateStoryPage";

// Pages Mobiles simplifiées (sans header Abracadaworld)
import MobileEvenementsPage from "./pages/mobile/MobileEvenementsPage";
import MobileGaleriePage from "./pages/mobile/MobileGaleriePage";
import MobileBlogPage from "./pages/mobile/MobileBlogPage";
import MobileSalonPage from "./pages/mobile/MobileSalonPage";
import MobileActuPage from "./pages/mobile/MobileActuPage";
import MobileArtisansPage from "./pages/mobile/MobileArtisansPage";
import MobileArtisansCartePage from "./pages/mobile/MobileArtisansCartePage";

// Cockpit Démo Pages
import DemoParticulierPage from "./pages/cockpit/demo/DemoParticulierPage";
import DemoProCockpitPage from "./pages/cockpit/demo/DemoProCockpitPage";

// Cockpit Particulier (Leboncoin style)
import CockpitParticulierLayout from "./pages/cockpit-particulier/CockpitParticulierLayout";
import CockpitParticulierDashboard from "./pages/cockpit-particulier/DashboardPage";
import CockpitParticulierAnnonces from "./pages/cockpit-particulier/MesAnnoncesPage";
import CockpitParticulierDeposer from "./pages/cockpit-particulier/DeposerAnnoncePage";
import CockpitParticulierMessages from "./pages/cockpit-particulier/MessagesPage";
import CockpitParticulierFavoris from "./pages/cockpit-particulier/FavorisPage";
import CockpitParticulierVentes from "./pages/cockpit-particulier/VentesPage";
import CockpitParticulierAchats from "./pages/cockpit-particulier/AchatsPage";
import CockpitParticulierVendeurs from "./pages/cockpit-particulier/VendeursSuivisPage";
import CockpitParticulierProfil from "./pages/cockpit-particulier/ProfilPage";
import CockpitParticulierParametres from "./pages/cockpit-particulier/ParametresPage";
import CockpitParticulierMessagerie from "./pages/cockpit-particulier/MessagerieParticulierPage";
import CockpitParticulierTaches from "./pages/cockpit-particulier/TachesKanbanPage";
import CockpitParticulierGroupes from "./pages/cockpit-particulier/GroupesPage";
import CockpitParticulierGroupDetail from "./pages/cockpit-particulier/GroupDetailPage";

// Cockpit Particulier - Marketplace
import CockpitMarketplaceDashboard from "./pages/cockpit-particulier/marketplace/DashboardMarketplace";
import CockpitMarketplaceCommandes from "./pages/cockpit-particulier/marketplace/CommandesPage";
import CockpitMarketplaceFavoris from "./pages/cockpit-particulier/marketplace/FavorisMarketplacePage";
import CockpitMarketplaceAvis from "./pages/cockpit-particulier/marketplace/AvisMarketplacePage";

// Cockpit Particulier - Artisans
import CockpitArtisansDashboard from "./pages/cockpit-particulier/artisans/DashboardArtisans";
import CockpitArtisansDevis from "./pages/cockpit-particulier/artisans/DevisPage";
import CockpitArtisansChantiers from "./pages/cockpit-particulier/artisans/ChantiersPage";
import CockpitArtisansFavoris from "./pages/cockpit-particulier/artisans/ArtisansFavoris";
import CockpitArtisansAvis from "./pages/cockpit-particulier/artisans/AvisArtisans";

// Cockpit Particulier - Réseau Social
import CockpitReseauFeed from "./pages/cockpit-particulier/reseau/FeedReseau";
import CockpitReseauPosts from "./pages/cockpit-particulier/reseau/MesPublications";
import CockpitReseauGalerie from "./pages/cockpit-particulier/reseau/MaGalerie";
import CockpitReseauAbonnes from "./pages/cockpit-particulier/reseau/MesAbonnes";
import CockpitReseauAbonnements from "./pages/cockpit-particulier/reseau/MesAbonnements";
import CockpitReseauMessages from "./pages/cockpit-particulier/reseau/MessagesReseau";
import CockpitReseauEvenements from "./pages/cockpit-particulier/reseau/EvenementsPage";
import CockpitReseauSalons from "./pages/cockpit-particulier/reseau/SalonsPage";
import CockpitReseauNotifications from "./pages/cockpit-particulier/reseau/NotificationsPage";
import CockpitReseauBlog from "./pages/cockpit-particulier/reseau/BlogPage";

// Cockpit Particulier - Emploi
import CockpitEmploiDashboard from "./pages/cockpit-particulier/emploi/DashboardEmploi";
import CockpitEmploiOffres from "./pages/cockpit-particulier/emploi/OffresPage";
import CockpitEmploiCandidatures from "./pages/cockpit-particulier/emploi/CandidaturesPage";
import CockpitEmploiCV from "./pages/cockpit-particulier/emploi/CVPage";
import CockpitEmploiAlertes from "./pages/cockpit-particulier/emploi/AlertesPage";

// Cockpit Particulier - Cercles (Groupes privés)
import CerclesPage from "./pages/cockpit-particulier/CerclesPage";

// Cockpit Particulier - Mon Cercle de Confiance (Super Confiance users)
import MonCercleConfiancePage from "./pages/cockpit-particulier/MonCercleConfiancePage";

// Cockpit Particulier - Mes Notes
import MesNotesPage from "./pages/cockpit-particulier/MesNotesPage";

// Cockpit Particulier - Boîte à idées
import BoiteAIdeesPage from "./pages/cockpit-particulier/BoiteAIdeesPage";

// Cockpit Particulier - Agenda & Visioconférence Google
import AgendaPage from "./pages/cockpit-particulier/AgendaPage";
import VisioconferencePage from "./pages/cockpit-particulier/VisioconferencePage";

// Cockpit Particulier - Mails & Documents Google
import MailsPage from "./pages/cockpit-particulier/MailsPage";
import DocumentsParticulierPage from "./pages/cockpit-particulier/DocumentsPage";

// Cockpit Pro - Google Workspace (Mails, Documents, Agenda, Visio)
import MailsProPage from "./pages/cockpit-pro/MailsProPage";
import DocumentsProPage from "./pages/cockpit-pro/DocumentsProPage";
import AgendaProPage from "./pages/cockpit-pro/AgendaProPage";
import VisioProPage from "./pages/cockpit-pro/VisioProPage";
import FormulesPage from "./pages/cockpit-pro/FormulesPage";
import PaiementsConnectPage from "./pages/cockpit-pro/PaiementsConnectPage";

// Super Assistant IA - Cockpit
import AssistantParticulierPage from "./pages/cockpit-particulier/AssistantPage";
import AssistantProPage from "./pages/cockpit-pro/AssistantProPage";

// Abra IA - Chat avec Artifacts (style Claude)
import AbraIAProPage from "./pages/cockpit-pro/AbraIAPage";
import AbraIAParticulierPage from "./pages/cockpit-particulier/AbraIAPage";

// AbracaCoins - Tarifs et recharge
import TarifsAbracaCoinsPage from "./pages/abracacoins/TarifsAbracaCoinsPage";
import MesOptionsPage from "./pages/cockpit-pro/MesOptionsPage";
import MentionsLegalesPage from "./pages/cockpit-pro/parametres/MentionsLegalesPage";
import DocumentsLegauxPage from "./pages/cockpit-pro/parametres/DocumentsLegauxPage";
import KnowledgeBasePage from "./pages/cockpit-pro/parametres/KnowledgeBasePage";
import ResponseAssistantPage from "./pages/cockpit-pro/ResponseAssistantPage";
import WizardAbraPage from "./pages/cockpit-pro/WizardAbraPage";

// Plateforme - Superadmin
import PlateformeDashboardPage from "./pages/cockpit-pro/plateforme/PlateformeDashboardPage";
import PlateformeAbonnementsPage from "./pages/cockpit-pro/plateforme/PlateformeAbonnementsPage";
import CercleConfiancePage from "./pages/cockpit-pro/plateforme/CercleConfiancePage";

// AbracadaTchat - Messagerie
import AbracadaTchatPage from "./pages/messages/MessagesPage";
import VerifyIdentityPage from "./pages/ecosystem/social/VerifyIdentityPage";
import VerificationAppointmentPage from "./pages/ecosystem/social/VerificationAppointmentPage";
import UpgradeProPage from "./pages/ecosystem/social/UpgradeProPage";
import ChantiersMapPage from "./pages/ecosystem/social/ChantiersMapPage";
import OldJobsPage from "./pages/ecosystem/social/JobsPage";
import EventsPage from "./pages/ecosystem/social/EventsPage";
import ArtisansPage from "./pages/ecosystem/marketplace/ArtisansPage";
import Artisans5EtoilesPage from "./pages/ecosystem/marketplace/Artisans5EtoilesPage";
import ShopDetailPage from "./pages/ecosystem/marketplace/ShopDetailPage";
import MarketplacePage from "./pages/ecosystem/listings/MarketplacePage";
import ListingDetailPage from "./pages/ecosystem/listings/ListingDetailPage";
import CreateListingPage from "./pages/ecosystem/listings/CreateListingPage";

// Emploi - Page optimisée style Indeed
import JobsPage from "./pages/emploi/JobsPage";
import MobileEmploiPage from "./pages/mobile/MobileEmploiPage";
import MobileCinemaPage from "./pages/mobile/MobileCinemaPage";

// Desktop Pages pour Réseau (Galerie, Salon, Événements, Cinéma)
import DesktopGaleriePage from "./pages/desktop/DesktopGaleriePage";
import DesktopBlogPage from "./pages/desktop/DesktopBlogPage";
import DesktopSalonPage from "./pages/desktop/DesktopSalonPage";
import DesktopEvenementsPage from "./pages/desktop/DesktopEvenementsPage";
import DesktopCinemaPage from "./pages/desktop/DesktopCinemaPage";

// Abracada.app - Point d'entrée pour les Cockpits
import AbracadaAppEntry from "./pages/abracada-app/AbracadaAppEntry";
import CockpitProPreview from "./pages/abracada-app/CockpitProPreview";

// Layout Unifié - Design System
import { UnifiedLayout, MobileNavWrapper } from "./components/layout";

// Univers - Les 6 univers principaux
import MarketplaceProduitsPage from "./pages/univers/MarketplaceProduitsPage";
import MarketplaceUnifiePage from "./pages/univers/MarketplaceUnifiePage";
import MobileMarketplacePage from "./pages/mobile/MobileMarketplacePage";
import MobilePrestationsPage from "./pages/mobile/MobilePrestationsPage";
import DesktopPrestationsPage from "./pages/ecosystem/marketplace/PrestationsPage";
import PrestationDetailPage from "./pages/ecosystem/marketplace/PrestationDetailPage";
import MobileEntreParticuliersPage from "./pages/mobile/MobileEntreParticuliersPage";
import EntreParticuliersPage from "./pages/univers/EntreParticuliersPage";
import ProduitDetailPage from "./pages/univers/ProduitDetailPage";
import PetitesAnnoncesPage from "./pages/univers/PetitesAnnoncesPage";
import AnnonceDetailPage from "./pages/univers/AnnonceDetailPage";
import FavorisAnnoncesPage from "./pages/univers/FavorisAnnoncesPage";
import CommandesAnnoncesPage from "./pages/univers/CommandesAnnoncesPage";
import ReseauProsPage from "./pages/reseau-pros/ReseauProsPage";
import ParticuliersPage from "./pages/univers/ParticuliersPage";

// Page Univers - Sélection des univers AbracadaWorld
import UniversPage from "./pages/UniversPage";

// Espace Client Vendeur/Acheteur (Compte)
import CompteLayout from "./pages/compte/CompteLayout";
import CompteDashboardPage from "./pages/compte/DashboardPage";
import CompteProduitsPage from "./pages/compte/ProduitsPage";
import CompteCommandesPage from "./pages/compte/CommandesPage";
import CompteLivraisonsPage from "./pages/compte/LivraisonsPage";
import CompteMessagesPage from "./pages/compte/MessagesPage";
import CompteTicketsPage from "./pages/compte/TicketsPage";
import ComptePaiementsPage from "./pages/compte/PaiementsPage";
import CompteMaBoutiquePage from "./pages/compte/MaBoutiquePage";
import CompteListesPage from "./pages/compte/ListesPage";
import CompteVentesFlashPage from "./pages/compte/VentesFlashPage";
import CompteAvisPage from "./pages/compte/AvisPage";
import CompteDevisPage from "./pages/compte/DevisPage";
import CompteOffresEmploiPage from "./pages/compte/OffresEmploiPage";
import CreationProfilPage from "./pages/compte/CreationProfilPage";

// Pages Publiques Acheteurs
import VentesFlashPublicPage from "./pages/VentesFlashPublicPage";
import MeilleuresVentesPage from "./pages/MeilleuresVentesPage";
import NouveautesPage from "./pages/NouveautesPage";
import MesCommandesPage from "./pages/acheteur/MesCommandesPage";
import SuiviCommandePage from "./pages/acheteur/SuiviCommandePage";
import MesFavorisPage from "./pages/acheteur/MesFavorisPage";
import MonProfilPage from "./pages/acheteur/MonProfilPage";
import PromotionsPage from "./pages/acheteur/PromotionsPage";
import CartesPage from "./pages/acheteur/CartesPage";
import ParametresPage from "./pages/acheteur/ParametresPage";
import ServiceClientPage from "./pages/acheteur/ServiceClientPage";
import AcheterANouveauPage from "./pages/acheteur/AcheterANouveauPage";
import ConnexionAcheteurPage from "./pages/acheteur/ConnexionAcheteurPage";
import PanierPage from "./pages/acheteur/PanierPage";
import CheckoutPage from "./pages/acheteur/CheckoutPage";

// Layout Acheteur (menu latéral sur desktop)
import AcheteurLayout from "./layouts/AcheteurLayout";

// Public Shop Page
import PublicShopPage from "./pages/public/PublicShopPage";

// Espace Pro - Nouvelles pages
import EspaceProLandingPage from "./pages/espacepro/EspaceProLandingPage";
import EspaceProFondateurPage from "./pages/espacepro/EspaceProFondateurPage";
import MembresFondateursPage from "./pages/espacepro/MembresFondateursPage";
import EspaceProTarifsPage from "./pages/espacepro/EspaceProTarifsPage";
import EspaceProCRMPage from "./pages/espacepro/EspaceProCRMPage";
import EspaceProAssistantPage from "./pages/espacepro/EspaceProAssistantPage";
import EquipeAgentsIAPage from "./pages/espacepro/EquipeAgentsIAPage";
import BoutiquePage from "./pages/espacepro/BoutiquePage";

// Espace Pro - Pages BatiAssist (wrappées dans le layout Abracadabati)
import BatiAssistPresentationPage from "./pages/espacepro/BatiAssistPresentationPage";
import BatiAssistCRMPage from "./pages/espacepro/BatiAssistCRMPage";
import BatiAssistDevisPage from "./pages/espacepro/BatiAssistDevisPage";
import BatiAssistPipelinesPage from "./pages/espacepro/BatiAssistPipelinesPage";
import BatiAssistAutomatisationPage from "./pages/espacepro/BatiAssistAutomatisationPage";
import BatiAssistAssistantsPage from "./pages/espacepro/BatiAssistAssistantsPage";
import BatiAssistTarifsPage from "./pages/espacepro/BatiAssistTarifsPage";
import DiagnosticEntrepreneurWrapperPage from "./pages/espacepro/DiagnosticEntrepreneurWrapperPage";

// Univers AbracadaWorld - SUPPRIME (pivot vers Abracada Bati)
// import UniversePage from "./pages/univers/UniversePage";

// Layout
import Layout from "./components/Layout";
import AdminLayout from "./components/AdminLayout";

// TopBanner global
import TopBanner from "./components/ui/TopBanner";

function App() {
  return (
    <HelmetProvider>
    <BrowserRouter>
      <TenantProvider>
      <ThemeProvider>
      <NavModeProvider>
      <ImmersiveScrollProvider>
      <AppModeProvider>
      <BuyerProvider>
      <DemoModeProvider>
        {/* Bandeau global en haut de tout le site */}
        <TopBanner />
        
        {/* FAB Cockpit - Unique bouton flottant */}
        <CockpitFAB />
        
        {/* Widget Preview Cockpit - Desktop uniquement */}
        <CockpitPreviewWidget />
        
        <ScrollToTop />
        <ModeVisiteur />
        <Toaster position="top-center" richColors expand={true} />
        <PWAComponents />
        <ErrorBoundary>
        <Routes>
        {/* Abracada Bati - Page d'accueil principale (sans Layout) */}
        <Route path="/" element={<AccueilPorteBati />} />
        <Route path="/abracadaworld" element={<AbracadaWorldHome />} />
        <Route path="/recherche" element={<SearchPage />} />
        <Route path="/decrire-projet" element={<DecrireProjetPage />} />
        <Route path="/offre-fondateur" element={<OffreFondateurPage />} />
        
        {/* Assistant IA Full Page (style Claude/ChatGPT) */}
        <Route path="/assistant" element={<AssistantFullPage />} />
        <Route path="/abra" element={<AssistantFullPage />} />
        
        {/* Pages de paiement Stripe */}
        <Route path="/paiement/succes" element={<PaymentSuccessPage />} />
        <Route path="/paiement/annule" element={<PaymentCancelledPage />} />
        
        {/* Rejoindre un cercle via lien d'invitation */}
        <Route path="/rejoindre/:inviteCode" element={<JoinCirclePage />} />
        <Route path="/rejoindre-groupe/:inviteCode" element={<JoinGroupPage />} />
        <Route path="/rejoindre-equipe/:inviteCode" element={<RejoindreEquipePage />} />
        <Route path="/collab/join/:token" element={<JoinCollabPage />} />
        
        {/* ══════════════════════════════════════════════════════════════════════
            ABRACADA.APP - Point d'entrée pour les Cockpits
            ══════════════════════════════════════════════════════════════════════ */}
        <Route path="/app" element={<AbracadaAppEntry />} />
        <Route path="/cockpit-pro-preview" element={<CockpitProPreview />} />
        
        {/* ══════════════════════════════════════════════════════════════════════
            BOUTIQUE PRO - Pages publiques des professionnels
            ══════════════════════════════════════════════════════════════════════ */}
        <Route path="/pro/:slug" element={<PublicStorePage />} />
        <Route path="/boutique/:shopId" element={<ShopDetailPage />} />
        
        {/* Espace Pro - Mini-site dédié aux professionnels */}
        <Route path="/pro" element={<EspaceProLandingPage />} />
        <Route path="/pro/fondateur" element={<EspaceProFondateurPage />} />
        <Route path="/pro/membres-fondateurs" element={<MembresFondateursPage />} />
        <Route path="/pro/tarifs" element={<EspaceProTarifsPage />} />
        <Route path="/pro/boutique" element={<BoutiquePage />} />
        <Route path="/pro/crm-batiment" element={<EspaceProCRMPage />} />
        <Route path="/pro/equipe-agents-ia" element={<EquipeAgentsIAPage />} />
        
        {/* Redirections anciennes URLs /espace-pro → /pro */}
        <Route path="/espace-pro" element={<Navigate to="/pro" replace />} />
        <Route path="/espace-pro/*" element={<Navigate to="/pro" replace />} />
        
        {/* BatiAssist - Pages wrappées dans le layout Abracadabati (accès depuis Espace Pro) */}
        <Route path="/batiassist" element={<BatiAssistPresentationPage />} />
        <Route path="/batiassist/crm" element={<BatiAssistCRMPage />} />
        <Route path="/batiassist/devis" element={<BatiAssistDevisPage />} />
        <Route path="/batiassist/pipelines" element={<BatiAssistPipelinesPage />} />
        <Route path="/batiassist/automatisation" element={<BatiAssistAutomatisationPage />} />
        <Route path="/batiassist/assistants" element={<BatiAssistAssistantsPage />} />
        <Route path="/batiassist/tarifs" element={<BatiAssistTarifsPage />} />
        <Route path="/diagnostic-entrepreneur" element={<DiagnosticEntrepreneurWrapperPage />} />
        
        {/* Public Routes - BatiAssist (ancienne landing - REDIRIGE vers nouvelles routes) */}
        <Route element={<Layout />}>
          <Route path="/equipe-digitale" element={<DigitalTeamPage />} />
          <Route path="/machine-clients" element={<NewClientMachinePage />} />
          <Route path="/transformation" element={<TransformationPage />} />
          <Route path="/faq" element={<FAQPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/tarifs" element={<PricingPage />} />
          <Route path="/super-assistant" element={<SuperAssistantPage />} />
          <Route path="/cout-salarie-france" element={<SalaryCostPage />} />
        </Route>

        {/* Feature Pages - Sans Layout car elles ont leur propre Header/Footer */}
        <Route path="/a-propos" element={<AboutPage />} />
        <Route path="/nos-solutions" element={<NosSolutionsPage />} />
        <Route path="/fonctionnalites/crm-batiment" element={<CRMBatimentPage />} />
        <Route path="/fonctionnalites/devis-intelligents" element={<DevisIntellPage />} />
        <Route path="/fonctionnalites/pipelines" element={<PipelinesPage />} />
        <Route path="/fonctionnalites/geolocalisation" element={<GeolocalisationPage />} />
        <Route path="/fonctionnalites/assistants" element={<AssistantsPage />} />
        <Route path="/fonctionnalites/automatisation-ia" element={<AutomatisationPage />} />
        <Route path="/fonctionnalites/suivi-paiements" element={<SuiviPaiementsPage />} />

        {/* Route univers supprimee - Redirection vers artisans */}
        {/* Les anciennes routes /univers/* redirigent maintenant vers /artisans */}

        {/* Public Routes Without Layout */}
        <Route path="/devis/:quoteId" element={<PublicQuoteSignPage />} />
        <Route path="/mon-chantier/:token" element={<ClientPortalPage />} />
        <Route path="/mon-devis/:token" element={<ProspectPortalPage />} />
        <Route path="/espace-client-portail/:token" element={<ClientPortalDevisPage />} />

        {/* Admin Routes */}
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route element={<AdminLayout />}>
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/admin/blog" element={<AdminBlog />} />
          <Route path="/admin/blog/new" element={<AdminBlogEditor />} />
          <Route path="/admin/blog/edit/:id" element={<AdminBlogEditor />} />
          <Route path="/admin/contacts" element={<AdminContacts />} />
          <Route path="/admin/newsletter" element={<AdminNewsletter />} />
          <Route path="/admin/leads" element={<AdminLeads />} />
          {/* Admin CRM Routes */}
          <Route path="/admin/crm" element={<AdminCRMDashboard />} />
          <Route path="/admin/crm/prospects" element={<AdminProspectsPage />} />
          <Route path="/admin/crm/prospects/new" element={<AdminProspectsPage />} />
          <Route path="/admin/crm/prospects/:id" element={<AdminProspectDetailPage />} />
          <Route path="/admin/crm/pipeline" element={<AdminPipelinePage />} />
          <Route path="/admin/crm/quotes" element={<AdminQuotesPage />} />
          <Route path="/admin/crm/quotes/:id" element={<AdminQuotesPage />} />
          <Route path="/admin/crm/clients" element={<AdminClientsPage />} />
          <Route path="/admin/crm/clients/:id" element={<AdminClientsPage />} />
          <Route path="/admin/crm/tickets" element={<AdminTicketsPage />} />
          <Route path="/admin/crm/invoices" element={<AdminInvoicesPage />} />
          {/* Admin Resources Routes */}
          <Route path="/admin/resources/assistants" element={<AdminAssistantsPage />} />
          <Route path="/admin/resources/candidates" element={<AdminCandidatesPage />} />
          <Route path="/admin/resources/chat" element={<AdminTeamChatPage />} />
          <Route path="/admin/resources/tasks" element={<AdminTasksPage />} />
          <Route path="/admin/resources/client-chat" element={<AdminClientChatPage />} />
          {/* Direction Commerciale Routes */}
          <Route path="/admin/commercial" element={<CommercialDashboard />} />
          <Route path="/admin/commercial/pipeline/:pipelineType" element={<PipelineKanban />} />
          {/* Publicités */}
          <Route path="/admin/ads" element={<AdminAdsPage />} />
        </Route>

        {/* Client Portal Routes */}
        <Route path="/espace-client" element={<ClientLoginPage />} />
        <Route element={<ClientLayout />}>
          <Route path="/espace-client/dashboard" element={<ClientDashboard />} />
          <Route path="/espace-client/messages" element={<ClientMessages />} />
          <Route path="/espace-client/tasks" element={<ClientTasks />} />
          <Route path="/espace-client/calendar" element={<CalendarPage />} />
          <Route path="/espace-client/team" element={<TeamPage />} />
          <Route path="/espace-client/team-chat" element={<ClientTeamChatPage />} />
          <Route path="/espace-client/crm" element={<CRMPage />} />
          <Route path="/espace-client/billing" element={<BillingPage />} />
          <Route path="/espace-client/users" element={<UsersPage />} />
          <Route path="/espace-client/blog" element={<BlogManagePage />} />
          {/* ════════════════════════════════════════════════════════════════════
              CRM CORE - Routes universelles pour tous les pros
              ════════════════════════════════════════════════════════════════════ */}
          <Route path="/espace-client/crm" element={<CRMDashboard />} />
          <Route path="/espace-client/crm/prospects" element={<ProspectsPage />} />
          <Route path="/espace-client/crm/prospects/:id" element={<ProspectsPage />} />
          <Route path="/espace-client/crm/pipeline" element={<CommercialPipeline />} />
          <Route path="/espace-client/crm/clients" element={<ClientsPage />} />
          <Route path="/espace-client/crm/devis" element={<DevisPage />} />
          <Route path="/espace-client/crm/devis/:id" element={<DevisPage />} />
          <Route path="/espace-client/crm/factures" element={<FacturesPage />} />
          <Route path="/espace-client/crm/produits" element={<ProduitsPage />} />
          <Route path="/espace-client/crm/prestations" element={<PrestationsPage />} />
          <Route path="/espace-client/crm/tickets" element={<TicketsSAVPage />} />
          
          {/* ════════════════════════════════════════════════════════════════════
              MODULE CHANTIERS - Add-on BTP
              ════════════════════════════════════════════════════════════════════ */}
          <Route path="/espace-client/chantiers" element={<ChantiersPage />} />
          <Route path="/espace-client/chantiers/compte-rendu" element={<CompteRenduChantierPage />} />
          <Route path="/espace-client/chantiers/planning" element={<PlanningPage />} />
          <Route path="/espace-client/chantiers/rentabilite" element={<RentabiliteDashboard />} />
          <Route path="/espace-client/chantiers/documents" element={<DocumentsPage />} />
          <Route path="/espace-client/chantiers/documents/:chantierId" element={<DocumentsPage />} />
          
          {/* ════════════════════════════════════════════════════════════════════
              LEGACY ROUTES - Rétrocompatibilité /crm-batiment/* 
              ════════════════════════════════════════════════════════════════════ */}
          <Route path="/espace-client/crm-batiment" element={<CRMDashboard />} />
          <Route path="/espace-client/crm-batiment/dashboard" element={<CRMDashboard />} />
          <Route path="/espace-client/crm-batiment/prospects" element={<ProspectsPage />} />
          <Route path="/espace-client/crm-batiment/prospects/:id" element={<ProspectsPage />} />
          <Route path="/espace-client/crm-batiment/pipeline" element={<CommercialPipeline />} />
          <Route path="/espace-client/crm-batiment/clients" element={<ClientsPage />} />
          <Route path="/espace-client/crm-batiment/devis" element={<DevisPage />} />
          <Route path="/espace-client/crm-batiment/devis/:id" element={<DevisPage />} />
          <Route path="/espace-client/crm-batiment/factures" element={<FacturesPage />} />
          <Route path="/espace-client/crm-batiment/produits" element={<ProduitsPage />} />
          <Route path="/espace-client/crm-batiment/prestations" element={<PrestationsPage />} />
          <Route path="/espace-client/crm-batiment/boutique-pro" element={<BoutiqueProPage />} />
          <Route path="/espace-client/crm-batiment/tickets" element={<TicketsSAVPage />} />
          <Route path="/espace-client/crm-batiment/chantiers" element={<ChantiersPage />} />
          <Route path="/espace-client/crm-batiment/chantiers/compte-rendu" element={<CompteRenduChantierPage />} />
          <Route path="/espace-client/crm-batiment/planning" element={<PlanningPage />} />
          <Route path="/espace-client/crm-batiment/rentabilite" element={<RentabiliteDashboard />} />
          <Route path="/espace-client/crm-batiment/rentabilite-old" element={<RentabilitePage />} />
          <Route path="/espace-client/crm-batiment/documents" element={<DocumentsPage />} />
          <Route path="/espace-client/crm-batiment/documents/:chantierId" element={<DocumentsPage />} />
          {/* Settings */}
          <Route path="/espace-client/settings/notifications" element={<NotificationSettingsPage />} />
          <Route path="/espace-client/settings/connected-accounts" element={<ConnectedAccountsPage />} />
          <Route path="/espace-client/settings/hourly-rates" element={<HourlyRatesPage />} />
          
          {/* ══════════════════════════════════════════════════════════════════════
              MA BOUTIQUE - Configuration vitrine Pro
              ══════════════════════════════════════════════════════════════════════ */}
          <Route path="/espace-client/ma-boutique" element={<MyStorePage />} />
          
          {/* Pointage */}
          <Route path="/espace-client/pointage" element={<PointagePage />} />
          {/* Marketing */}
          <Route path="/espace-client/marketing" element={<MarketingDashboard />} />
          <Route path="/espace-client/marketing/accounts" element={<SocialAccountsPage />} />
          <Route path="/espace-client/marketing/calendar" element={<ContentCalendarPage />} />
          <Route path="/espace-client/marketing/engagement" element={<EngagementPage />} />
          <Route path="/espace-client/marketing/ai-agents" element={<AIAgentsPage />} />
          {/* AI Vendor */}
          <Route path="/espace-client/ai-vendor" element={<AIVendorDashboard />} />
          <Route path="/espace-client/ai-vendor/catalog" element={<CatalogPage />} />
          <Route path="/espace-client/ai-vendor/settings" element={<VendorSettingsPage />} />
          <Route path="/espace-client/ai-vendor/conversations" element={<ConversationsPage />} />
          <Route path="/espace-client/ai-vendor/conversations/:conversationId" element={<ConversationsPage />} />
          <Route path="/espace-client/ai-vendor/widget" element={<WidgetPage />} />
          {/* Ma Boutique */}
          <Route path="/espace-client/boutique" element={<BoutiqueDashboard />} />
          <Route path="/espace-client/boutique/design" element={<BoutiqueDesignPage />} />
          <Route path="/espace-client/boutique/medias" element={<BoutiqueMediasPage />} />
          <Route path="/espace-client/boutique/avis" element={<BoutiqueAvisPage />} />
          {/* Email */}
          <Route path="/espace-client/emails" element={<EmailPage />} />
          {/* Calendar */}
          <Route path="/espace-client/shared-calendar" element={<SharedCalendarPage />} />
        </Route>

        {/* ══════════════════════════════════════════════════════════════════
            WIZARD ABRA - Onboarding Pro (plein écran)
            ══════════════════════════════════════════════════════════════════ */}
        <Route path="/cockpit-pro/wizard" element={<WizardAbraPage />} />
        
        {/* Gérer vos Formules - Page plein écran */}
        <Route path="/cockpit-pro/formules" element={<FormulesPage />} />

        {/* ══════════════════════════════════════════════════════════════════
            COCKPIT PRO - Nouveau Design unifié avec Cockpit Particulier
            ══════════════════════════════════════════════════════════════════ */}
        <Route path="/cockpit-pro" element={<CockpitProLayout />}>
          <Route index element={<CockpitProDashboard />} />
          <Route path="dashboard" element={<CockpitProDashboard />} />
          {/* Redirections pratiques */}
          <Route path="devis" element={<Navigate to="/cockpit-pro/commercial/devis" replace />} />
          <Route path="prospects" element={<Navigate to="/cockpit-pro/commercial/prospects" replace />} />
          <Route path="clients" element={<Navigate to="/cockpit-pro/commercial/clients" replace />} />
          {/* Collaboration / Équipes */}
          <Route path="collaboration/equipe" element={<EquipeProPage />} />
          <Route path="collaboration/taches" element={<TachesEquipeProPage />} />
          <Route path="collaboration/agenda" element={<SharedCalendarPage />} />
          <Route path="collaboration/tchat" element={<TchatProPage />} />
          <Route path="collaboration/feed" element={<FeedProPage />} />
          <Route path="collaboration/documents" element={<DocumentsPage />} />
          <Route path="collaboration/collabs" element={<CollabsProPage />} />
          <Route path="collaboration/pointage" element={<TimeClockPage />} />
          <Route path="collaboration/pointage-manager" element={<TimeClockManagerPage />} />
          {/* Commercial */}
          <Route path="commercial" element={<CRMDashboard />} />
          <Route path="commercial/demandes-devis" element={<DemandesDevisPage />} />
          <Route path="commercial/prospects" element={<ProspectsPage />} />
          <Route path="commercial/prospects/:id" element={<ProspectsPage />} />
          <Route path="commercial/pipeline" element={<CommercialPipeline />} />
          <Route path="commercial/clients" element={<ClientsPage />} />
          <Route path="commercial/devis" element={<DevisPage />} />
          <Route path="commercial/devis/:id" element={<DevisPage />} />
          <Route path="commercial/factures" element={<FacturesPage />} />
          <Route path="commercial/relances" element={<CRMDashboard />} />
          {/* Catalogue */}
          <Route path="catalogue" element={<ProduitsPage />} />
          <Route path="catalogue/produits" element={<ProduitsPage />} />
          <Route path="catalogue/prestations" element={<PrestationsPage />} />
          <Route path="catalogue/tarifs" element={<HourlyRatesPage />} />
          <Route path="catalogue/import" element={<ImportCSVPage />} />
          {/* Chantiers */}
          <Route path="chantiers" element={<ChantiersPage />} />
          <Route path="chantiers/en-cours" element={<ChantiersPage />} />
          <Route path="chantiers/a-venir" element={<ChantiersPage />} />
          <Route path="chantiers/termines" element={<ChantiersPage />} />
          <Route path="chantiers/planning" element={<PlanningPage />} />
          <Route path="chantiers/pointage" element={<PointagePage />} />
          <Route path="chantiers/compte-rendu" element={<CompteRenduChantierPage />} />
          {/* Marketing */}
          <Route path="marketing" element={<MarketingDashboard />} />
          <Route path="marketing/statistiques" element={<RentabiliteDashboard />} />
          <Route path="marketing/campagnes" element={<MarketingDashboard />} />
          <Route path="marketing/promotions" element={<MarketingDashboard />} />
          <Route path="marketing/ventes-flash" element={<MarketingDashboard />} />
          {/* Réseau Social */}
          <Route path="reseau-social" element={<ReseauSocialProPage />} />
          <Route path="reseau-social/posts" element={<CreatePostPage />} />
          <Route path="reseau-social/story/create" element={<CreateStoryPage />} />
          <Route path="reseau-social/galerie" element={<BoutiqueMediasPage />} />
          <Route path="reseau-social/abonnes" element={<ProfilePage />} />
          <Route path="reseau-social/messages" element={<MessagesPage />} />
          {/* Réseaux Sociaux Externes — Social Hub */}
          <Route path="reseaux-sociaux" element={<SocialHubPage />} />
          <Route path="reseaux-sociaux/publications" element={<SocialHubPage />} />
          <Route path="reseaux-sociaux/messages" element={<SocialHubPage />} />
          <Route path="reseaux-sociaux/statistiques" element={<SocialHubPage />} />
          {/* Réseau Pro B2B */}
          <Route path="reseau-pro" element={<ReseauProPage />} />
          <Route path="reseau-pro/annuaire" element={<ReseauProPage />} />
          <Route path="reseau-pro/partenaires" element={<ReseauProPage />} />
          <Route path="reseau-pro/opportunites" element={<ReseauProPage />} />
          <Route path="reseau-pro/evenements" element={<EventsPage />} />
          {/* Emploi Pro */}
          <Route path="emploi" element={<EmploiProPage />} />
          <Route path="emploi/offres" element={<EmploiProPage />} />
          <Route path="emploi/mes-offres" element={<EmploiProPage />} />
          <Route path="emploi/candidatures" element={<EmploiProPage />} />
          {/* Boutique */}
          <Route path="boutique" element={<BoutiqueProPage />} />
          <Route path="boutique/produits" element={<ProduitsPage />} />
          <Route path="boutique/informations" element={<BoutiqueProPage />} />
          <Route path="boutique/visuels" element={<BoutiqueMediasPage />} />
          <Route path="boutique/a-propos" element={<BoutiqueProPage />} />
          <Route path="boutique/mentions-legales" element={<MentionsLegalesPage />} />
          <Route path="boutique/base-connaissances" element={<KnowledgeBasePage />} />
          <Route path="parametres/base-connaissances" element={<KnowledgeBasePage />} />
          <Route path="assistant-reponses" element={<ResponseAssistantPage />} />
          <Route path="boutique/documents-legaux" element={<DocumentsLegauxPage />} />
          <Route path="boutique/horaires" element={<BoutiqueProPage />} />
          <Route path="boutique/blog" element={<BlogManagePage />} />
          {/* Paiements Stripe Connect */}
          <Route path="paiements" element={<PaiementsConnectPage />} />
          {/* Admin */}
          <Route path="admin" element={<UsersPage />} />
          <Route path="admin/abonnement" element={<BillingPage />} />
          <Route path="admin/facturation" element={<BillingPage />} />
          <Route path="admin/securite" element={<UsersPage />} />
          <Route path="admin/notifications" element={<NotificationSettingsPage />} />
          
          {/* ══════════════════════════════════════════════════════════════════
              PLATEFORME - Superadmin Only (Gestion AbracadaWorld)
          ══════════════════════════════════════════════════════════════════ */}
          <Route path="plateforme" element={<PlateformeDashboardPage />} />
          <Route path="plateforme/cercle-confiance" element={<CercleConfiancePage />} />
          <Route path="plateforme/abonnements" element={<PlateformeAbonnementsPage />} />
          <Route path="plateforme/vendeurs" element={<PlateformeDashboardPage />} />
          <Route path="plateforme/utilisateurs" element={<PlateformeDashboardPage />} />
          <Route path="plateforme/analytics" element={<PlateformeDashboardPage />} />
          <Route path="plateforme/moderation" element={<PlateformeDashboardPage />} />
          <Route path="plateforme/tickets" element={<PlateformeDashboardPage />} />
          <Route path="plateforme/factures" element={<PlateformeDashboardPage />} />
          <Route path="plateforme/parametres" element={<PlateformeDashboardPage />} />
          
          {/* Messagerie */}
          <Route path="messagerie" element={<MessagerieProPage />} />
          <Route path="messagerie/:conversationId" element={<MessagerieProPage />} />
          {/* Google Workspace - Mails, Documents, Agenda, Visio */}
          <Route path="mails" element={<MailsProPage />} />
          <Route path="documents-google" element={<DocumentsProPage />} />
          <Route path="agenda" element={<AgendaProPage />} />
          <Route path="visio" element={<VisioProPage />} />
          {/* Super Assistant IA Pro */}
          <Route path="assistant" element={<AssistantProPage />} />
          {/* Abra IA - Chat avec Artifacts (style Claude) */}
          <Route path="abra-ia" element={<AbraIAProPage />} />
          {/* Options Premium (Addons) */}
          <Route path="options" element={<MesOptionsPage />} />
          {/* AbracaCoins - Tarifs */}
          <Route path="abracacoins" element={<TarifsAbracaCoinsPage />} />
          {/* Paramètres du compte Pro */}
          <Route path="parametres" element={<CockpitParticulierParametres />} />
          {/* Navigation Pro Top Bar */}
          <Route path="presentation" element={<EspaceProLandingPage />} />
          <Route path="info-boutique" element={<BoutiquePage />} />
          <Route path="agents-ia" element={<EquipeAgentsIAPage />} />
          <Route path="tarifs-pro" element={<EspaceProTarifsPage />} />
          <Route path="fondateurs" element={<MembresFondateursPage />} />
        </Route>

        {/* Ecosystem Routes (Centre Commercial Virtuel du Bâtiment) */}
        <Route path="/reseau/connexion" element={<Navigate to="/connexion" replace />} />
        <Route path="/reseau/inscription" element={<EcoRegisterPage />} />
        <Route path="/reseau/confirmer-email" element={<ConfirmEmailPage />} />
        <Route path="/reseau/chantiers" element={<ChantiersMapPage />} />
        
        {/* ══════════════════════════════════════════════════════════════════
            RÉSEAU SOCIAL - Pages mobiles avec header Abracadabati simplifié
            ══════════════════════════════════════════════════════════════════ */}
        
        {/* Pages mobiles avec header simplifié (prioritaires) */}
        <Route path="/reseau" element={<ResponsivePageWrapper DesktopComponent={ActuPage} MobileComponent={MobileActuPage} />} />
        <Route path="/reseau/actus" element={<ResponsivePageWrapper DesktopComponent={ActuPage} MobileComponent={MobileActuPage} />} />
        <Route path="/reseau/galerie" element={<ResponsivePageWrapper DesktopComponent={DesktopGaleriePage} MobileComponent={MobileGaleriePage} />} />
        <Route path="/reseau/salon" element={<ResponsivePageWrapper DesktopComponent={DesktopSalonPage} MobileComponent={MobileSalonPage} />} />
        <Route path="/reseau/evenements" element={<ResponsivePageWrapper DesktopComponent={DesktopEvenementsPage} MobileComponent={MobileEvenementsPage} />} />
        <Route path="/reseau/cinema" element={<ResponsivePageWrapper DesktopComponent={DesktopCinemaPage} MobileComponent={MobileCinemaPage} />} />
        <Route path="/reseau/blog" element={<ResponsivePageWrapper DesktopComponent={DesktopBlogPage} MobileComponent={MobileBlogPage} />} />
        
        {/* Pages avec layout spécifique (messages, profil, etc.) */}
        <Route element={<ReseauLayout />}>
          <Route path="/reseau/messages" element={<MessagerieReseauPage />} />
          <Route path="/reseau/pages" element={<ReseauMobilePage />} />
          <Route path="/reseau/trending" element={<ReseauMobilePage />} />
          <Route path="/reseau/profil" element={<ProfilePage />} />
          <Route path="/reseau/profil/:username" element={<ProfilePage />} />
          <Route path="/reseau/messages/:conversationId" element={<MessagesPage />} />
          <Route path="/reseau/notifications" element={<CockpitReseauNotifications />} />
        </Route>
        
        {/* CINÉMA / REELS - PLEIN ÉCRAN TOTAL (hors layout) */}
        <Route path="/reseau/reels" element={<ReelsPage />} />
        
        <Route path="/reseau/publier" element={<CreatePostPage />} />
        <Route path="/reseau/decouvrir" element={<DiscoverPage />} />
        <Route path="/reseau/post/:postId" element={<PostDetailPage />} />
        <Route path="/reseau/verification" element={<VerifyIdentityPage />} />
        <Route path="/verification-rdv" element={<VerificationAppointmentPage />} />
        <Route path="/reseau/upgrade-pro" element={<UpgradeProPage />} />
        
        {/* Pages Marketplace avec EcosystemLayout */}
        <Route element={<EcosystemLayout />}>
          <Route path="/marketplace/annonce/:listingId" element={<ListingDetailPage />} />
          <Route path="/marketplace/deposer" element={<CreateListingPage />} />
        </Route>
        
        {/* Emploi - Page autonome style Indeed */}
        <Route path="/emploi" element={<ResponsivePageWrapper DesktopComponent={JobsPage} MobileComponent={MobileEmploiPage} />} />
        <Route path="/reseau/emploi" element={<JobsPage />} />
        
        {/* Page Univers - Sélection des univers AbracadaWorld */}
        <Route path="/univers" element={<UniversPage />} />
        
        {/* Les 6 Univers - Navigation principale avec Design System Unifié */}
        {/* Artisans mobile — standalone, en dehors du cockpit */}
        <Route path="/artisans" element={<MobileArtisansPage />} />
        <Route path="/artisans/carte" element={<MobileArtisansCartePage />} />
        <Route path="/artisans/5-etoiles" element={<Artisans5EtoilesPage />} />
        
        {/* Marketplace Unifiée */}
        <Route path="/marketplace" element={<ResponsivePageWrapper DesktopComponent={MarketplaceUnifiePage} MobileComponent={MobileMarketplacePage} />} />
        <Route path="/marketplace/rayons" element={<MarketplaceUnifiePage />} />
        <Route path="/marketplace/rayons/:rayonId" element={<MarketplaceUnifiePage />} />
        <Route path="/marketplace/ventes-flash" element={<MarketplaceUnifiePage />} />
        <Route path="/marketplace/meilleures-ventes" element={<MarketplaceUnifiePage />} />
        <Route path="/marketplace/nouveautes" element={<MarketplaceUnifiePage />} />
        <Route path="/produits" element={<Navigate to="/marketplace" replace />} />
        <Route path="/produits/:productId" element={<ProduitDetailPage />} />
        
        {/* Prestations - Recherche de services */}
        <Route path="/prestations" element={<ResponsivePageWrapper DesktopComponent={DesktopPrestationsPage} MobileComponent={MobilePrestationsPage} />} />
        <Route path="/prestations/:id" element={<PrestationDetailPage />} />
        
        {/* Entre Particuliers - Copie identique avec version mobile particuliers */}
        <Route path="/entre-particuliers" element={<ResponsivePageWrapper DesktopComponent={EntreParticuliersPage} MobileComponent={MobileEntreParticuliersPage} />} />
        <Route path="/entre-particuliers/deposer" element={<CreateListingPage />} />
        <Route path="/entre-particuliers/recentes" element={<ResponsivePageWrapper DesktopComponent={EntreParticuliersPage} MobileComponent={MobileEntreParticuliersPage} />} />
        <Route path="/entre-particuliers/urgentes" element={<ResponsivePageWrapper DesktopComponent={EntreParticuliersPage} MobileComponent={MobileEntreParticuliersPage} />} />
        
        {/* Déposer une annonce - Page standalone sans layout cockpit */}
        <Route path="/deposer-annonce" element={<CockpitParticulierDeposer />} />
        
        <Route path="/particuliers" element={<Navigate to="/entre-particuliers" replace />} />
        
        {/* Anciennes routes annonces - redirections */}
        <Route path="/annonces" element={<Navigate to="/marketplace" replace />} />
        <Route path="/annonces/favoris" element={<FavorisAnnoncesPage />} />
        <Route path="/annonces/commandes" element={<CommandesAnnoncesPage />} />
        <Route path="/annonces/deposer" element={<CreateListingPage />} />
        <Route path="/annonces/:annonceId" element={<AnnonceDetailPage />} />
        <Route path="/reseau-pros" element={<ReseauProsPage />} />

        {/* Pages Publiques Acheteurs - avec layout (menu latéral sur desktop) */}
        <Route element={<AcheteurLayout />}>
          <Route path="/mes-commandes" element={<MesCommandesPage />} />
          <Route path="/mes-favoris" element={<MesFavorisPage />} />
          <Route path="/mon-profil" element={<MonProfilPage />} />
          <Route path="/acheter-a-nouveau" element={<AcheterANouveauPage />} />
          <Route path="/promotions" element={<PromotionsPage />} />
          <Route path="/cartes-cadeaux" element={<CartesPage />} />
          <Route path="/parametres" element={<ParametresPage />} />
          <Route path="/service-client" element={<ServiceClientPage />} />
          <Route path="/aide" element={<ServiceClientPage />} />
        </Route>
        
        {/* Page suivi commande (hors layout) */}
        <Route path="/commande/:orderId" element={<SuiviCommandePage />} />
        
        {/* Pages sans layout latéral */}
        <Route path="/ventes-flash" element={<VentesFlashPublicPage />} />
        <Route path="/meilleures-ventes" element={<MeilleuresVentesPage />} />
        <Route path="/nouveautes" element={<NouveautesPage />} />
        <Route path="/connexion" element={<ConnexionAcheteurPage />} />
        <Route path="/connexion-acheteur" element={<ConnexionAcheteurPage />} />
        <Route path="/panier" element={<PanierPage />} />
        <Route path="/checkout" element={<CheckoutPage />} />
        
        {/* AbracadaTchat - Messagerie */}
        <Route path="/messages" element={<AbracadaTchatPage />} />
        <Route path="/messages/:conversationId" element={<AbracadaTchatPage />} />
        
        {/* Redirections pour URLs simplifiées */}
        <Route path="/inscription" element={<Navigate to="/reseau/inscription" replace />} />

        {/* Création profil Pro (sans layout) */}
        <Route path="/compte/creation" element={<CreationProfilPage />} />

        {/* Cockpit Démo Pages (sans authentification) */}
        <Route path="/cockpit/demo/particulier" element={<DemoParticulierPage />} />
        <Route path="/cockpit/demo/professionnel" element={<DemoProCockpitPage />} />

        {/* Cockpit Particulier (Leboncoin style - Espace vendeur C2C) */}
        <Route element={<CockpitParticulierLayout />}>
          {/* Entre Particuliers */}
          <Route path="/cockpit-particulier" element={<CockpitParticulierDashboard />} />
          <Route path="/cockpit-particulier/annonces" element={<CockpitParticulierAnnonces />} />
          <Route path="/cockpit-particulier/deposer" element={<CockpitParticulierDeposer />} />
          <Route path="/cockpit-particulier/messages" element={<CockpitParticulierMessages />} />
          <Route path="/cockpit-particulier/favoris" element={<CockpitParticulierFavoris />} />
          <Route path="/cockpit-particulier/ventes" element={<CockpitParticulierVentes />} />
          <Route path="/cockpit-particulier/achats" element={<CockpitParticulierAchats />} />
          <Route path="/cockpit-particulier/vendeurs-suivis" element={<CockpitParticulierVendeurs />} />
          <Route path="/cockpit-particulier/profil" element={<CockpitParticulierProfil />} />
          <Route path="/cockpit-particulier/parametres" element={<CockpitParticulierParametres />} />
          <Route path="/cockpit-particulier/messagerie" element={<CockpitParticulierMessagerie />} />
          <Route path="/cockpit-particulier/taches" element={<CockpitParticulierTaches />} />
          
          {/* Groupes / Famille */}
          <Route path="/cockpit-particulier/groupes" element={<CockpitParticulierGroupes />} />
          <Route path="/cockpit-particulier/groupes/:groupId" element={<CockpitParticulierGroupDetail />} />
          
          {/* Marketplace */}
          <Route path="/cockpit-particulier/marketplace" element={<CockpitMarketplaceDashboard />} />
          <Route path="/cockpit-particulier/marketplace/commandes" element={<CockpitMarketplaceCommandes />} />
          <Route path="/cockpit-particulier/marketplace/favoris" element={<CockpitMarketplaceFavoris />} />
          <Route path="/cockpit-particulier/marketplace/avis" element={<CockpitMarketplaceAvis />} />
          
          {/* Artisans */}
          <Route path="/cockpit-particulier/artisans" element={<CockpitArtisansDashboard />} />
          <Route path="/cockpit-particulier/artisans/devis" element={<CockpitArtisansDevis />} />
          <Route path="/cockpit-particulier/artisans/chantiers" element={<CockpitArtisansChantiers />} />
          <Route path="/cockpit-particulier/artisans/favoris" element={<CockpitArtisansFavoris />} />
          <Route path="/cockpit-particulier/artisans/avis" element={<CockpitArtisansAvis />} />


          <Route path="/artisans/:shopId" element={<ShopDetailPage />} />
          
          {/* Réseau Social */}
          <Route path="/cockpit-particulier/reseau" element={<CockpitReseauFeed />} />
          <Route path="/cockpit-particulier/reseau/posts" element={<CockpitReseauPosts />} />
          <Route path="/cockpit-particulier/reseau/blog" element={<CockpitReseauBlog />} />
          <Route path="/cockpit-particulier/reseau/galerie" element={<CockpitReseauGalerie />} />
          <Route path="/cockpit-particulier/reseau/abonnes" element={<CockpitReseauAbonnes />} />
          <Route path="/cockpit-particulier/reseau/abonnements" element={<CockpitReseauAbonnements />} />
          <Route path="/cockpit-particulier/reseau/messages" element={<CockpitReseauMessages />} />
          <Route path="/cockpit-particulier/reseau/evenements" element={<CockpitReseauEvenements />} />
          <Route path="/cockpit-particulier/reseau/salons" element={<CockpitReseauSalons />} />
          <Route path="/cockpit-particulier/reseau/notifications" element={<CockpitReseauNotifications />} />
          
          {/* Emploi */}
          <Route path="/cockpit-particulier/emploi" element={<CockpitEmploiDashboard />} />
          <Route path="/cockpit-particulier/emploi/offres" element={<CockpitEmploiOffres />} />
          <Route path="/cockpit-particulier/emploi/candidatures" element={<CockpitEmploiCandidatures />} />
          <Route path="/cockpit-particulier/emploi/cv" element={<CockpitEmploiCV />} />
          <Route path="/cockpit-particulier/emploi/alertes" element={<CockpitEmploiAlertes />} />
          
          {/* Cercles - Groupes privés */}
          <Route path="/cockpit-particulier/cercles" element={<CerclesPage />} />
          <Route path="/cockpit-particulier/mon-cercle-confiance" element={<MonCercleConfiancePage />} />
          
          {/* Mes Notes */}
          <Route path="/cockpit-particulier/notes" element={<MesNotesPage />} />
          
          {/* Boîte à idées */}
          <Route path="/cockpit-particulier/boite-a-idees" element={<BoiteAIdeesPage />} />
          
          {/* Agenda & Visioconférence Google */}
          <Route path="/cockpit-particulier/agenda" element={<AgendaPage />} />
          <Route path="/cockpit-particulier/visio" element={<VisioconferencePage />} />
          
          {/* Mails & Documents Google */}
          <Route path="/cockpit-particulier/mails" element={<MailsPage />} />
          <Route path="/cockpit-particulier/documents" element={<DocumentsParticulierPage />} />
          
          {/* Super Assistant IA Particulier */}
          <Route path="/cockpit-particulier/assistant" element={<AssistantParticulierPage />} />
          {/* Abra IA - Chat avec Artifacts (style Claude) */}
          <Route path="/cockpit-particulier/abra-ia" element={<AbraIAParticulierPage />} />
          {/* AbracaCoins - Tarifs */}
          <Route path="/cockpit-particulier/abracacoins" element={<TarifsAbracaCoinsPage />} />
        </Route>

        {/* Espace Pro (CRM) - Compte Vendeur/Artisan */}
        <Route element={<CompteLayout />}>
          <Route path="/compte" element={<CompteDashboardPage />} />
          <Route path="/compte/produits" element={<CompteProduitsPage />} />
          <Route path="/compte/produits/nouveau" element={<CompteProduitsPage />} />
          <Route path="/compte/produits/:id/modifier" element={<CompteProduitsPage />} />
          <Route path="/compte/commandes" element={<CompteCommandesPage />} />
          <Route path="/compte/livraisons" element={<CompteLivraisonsPage />} />
          <Route path="/compte/messages" element={<CompteMessagesPage />} />
          <Route path="/compte/tickets" element={<CompteTicketsPage />} />
          <Route path="/compte/paiements" element={<ComptePaiementsPage />} />
          <Route path="/compte/boutique" element={<CompteMaBoutiquePage />} />
          <Route path="/compte/listes" element={<CompteListesPage />} />
          <Route path="/compte/ventes-flash" element={<CompteVentesFlashPage />} />
          <Route path="/compte/avis" element={<CompteAvisPage />} />
          <Route path="/compte/devis" element={<CompteDevisPage />} />
          <Route path="/compte/offres-emploi" element={<CompteOffresEmploiPage />} />
          <Route path="/compte/favoris" element={<CompteListesPage />} />
          <Route path="/compte/historique" element={<CompteCommandesPage />} />
        </Route>
      </Routes>
      </ErrorBoundary>

      {/* Footer Global - Affiché sur toutes les pages */}
      <GlobalFooter />
      
      {/* Navigation Mobile Globale - Visible sur toutes les pages mobiles */}
      <MobileNavWrapper />
      
      {/* Popup de demande de notifications push */}
      <PushNotificationPrompt />
      
      </DemoModeProvider>
      </BuyerProvider>
      </AppModeProvider>
      </ImmersiveScrollProvider>
      </NavModeProvider>
      </ThemeProvider>
      </TenantProvider>
    </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
